import { convertToImage, convertToModel3d, convertToVideo } from "@/utils";
import { IComponentType, IFsFileType } from "@r3f-component-data-structure";
import { Box3, Vector3 } from "three";
import { IFile, IFileModel3D, IFileType } from "@zappar/zw-api-client";
import { filestore, newFsFileId } from "../store";
import { FsImage } from "./image";
import { FsModel3D } from "./model3d";
import { FsVideo } from "./video";
import { FsFile } from ".";
import { addSuffixToUrl } from "../utils";

interface FsExternalFileProps {
    source: FsExternalSource
    id: string
    name: string
    thumbnailUrl: string
    videoUrl?: string
    mp3Url?: string
    isAnimated?: boolean
    dimensions?: { x: number, y: number, z: number }
    aspectRatio?: number
    /** Raw downloaded file for a Local asset; persisted to the project filestore on import. */
    file?: File
}

/**
 * Where an asset's bytes come from, which decides how `import()` gets them into the project:
 * Local already has them in hand and PUTs them, ZML asks the CDS to copy them server-side.
 */
export enum FsExternalSource {
    Local = 'local',
    ZML = 'zml'
}

export class FsExternalFile {
    /**
     * Whose id this is depends on `source`: a ZML file id for ZML (what importFromZml resolves), a
     * freshly minted filestore file id for Local (what saveProjectFile stores the bytes under).
     */
    id: string
    source: FsExternalSource
    name: string
    type: IFsFileType
    thumbnailUrl: string
    videoUrl?: string
    mp3Url?: string
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    isAnimated: boolean = false
    dimensions: { x: number, y: number, z: number }
    aspectRatio: number | undefined
    file: File | undefined
    private _importedFile: FsFile | undefined

    constructor(type: IFsFileType, props: FsExternalFileProps) {
        this.id = props.id
        this.source = props.source
        this.name = props.name
        this.type = type
        this.thumbnailUrl = props.thumbnailUrl
        this.videoUrl = props.videoUrl
        this.mp3Url = props.mp3Url
        this.isAnimated = props.isAnimated || false
        this.dimensions = props.dimensions || { x: 0.5, y: 0.5, z: 0.5 }
        this.aspectRatio = props.aspectRatio
        this.file = props.file
    }

    async import<T extends FsFile>() {
        if (this.source === FsExternalSource.ZML) {
            const fsFile = await filestore.importFromZml(this.id)
            if (typeof fsFile === 'undefined') throw `Unable to import ${this.type} from ZML` // TODO: display error message to the user
            return fsFile as T
        }
        if (this.source === FsExternalSource.Local) {
            // Persist the downloaded file into the project filestore (never ZML). Cached so repeated
            // toSpatialComponent() calls (drag preview, then drop) don't re-upload.
            if (this._importedFile) return this._importedFile as T
            if (typeof this.file === 'undefined') throw new Error('FsExternalFile: no file to import for a local asset')
            const fsFile = await filestore.saveProjectFile(this.id, this.file, this.type, this.name)
            // Derive real bounding-box dimensions from the model so it scales correctly on the canvas.
            if (this.type === IFsFileType.Model3d) {
                const { parsedData } = await (fsFile as FsModel3D).getModelData()
                const size = new Box3().setFromObject(parsedData.scene).getSize(new Vector3())
                this.dimensions = { x: size.x || 0.5, y: size.y || 0.5, z: size.z || 0.5 }
            }
            this._importedFile = fsFile
            return fsFile as T
        }
        throw new Error(`Unsupported source: ${this.source}`)
    }

    async toSpatialComponent(compType?: IComponentType) {
        const fsFile = await this.import()
        if (typeof compType === 'undefined') {
            if (fsFile.type === IFsFileType.Image) {
                compType = IComponentType.Image
            } else if (fsFile.type === IFsFileType.Model3d) {
                compType = IComponentType.Model3d
            } else if (fsFile.type === IFsFileType.Video) {
                compType = IComponentType.Video
            }
        }
        if (compType === IComponentType.Image) return convertToImage(fsFile as FsImage)
        if (compType === IComponentType.Video) return convertToVideo(fsFile as FsVideo)
        if (compType === IComponentType.Model3d) return convertToModel3d(fsFile as FsModel3D, this.dimensions)
        throw `Unable to convert ${this.type} to spatial component ${compType}`
    }
}

export const zmlToExternalFile = (zmlFile: IFile) => {
    const opts = { source: FsExternalSource.ZML, id: zmlFile.id, name: zmlFile.filename, thumbnailUrl: '' }
    if (zmlFile.type === IFileType.Image) {
        return new FsExternalFile(IFsFileType.Image, { ...opts, thumbnailUrl: addSuffixToUrl(zmlFile.url, '/img') })
    }
    if (zmlFile.type === IFileType.Image360) {
        return new FsExternalFile(IFsFileType.Image360, { ...opts, thumbnailUrl: addSuffixToUrl(zmlFile.url, '/img') })
    }
    if (zmlFile.type === IFileType.Model3D) {
        return new FsExternalFile(IFsFileType.Model3d, {
            ...opts,
            thumbnailUrl: addSuffixToUrl(zmlFile.url, '/frame0.png'),
            isAnimated: Boolean((zmlFile as IFileModel3D).meta?.animations?.length),
            dimensions: (zmlFile as IFileModel3D).meta?.dimensions
        })
    }
    if (zmlFile.type === IFileType.StreamingVideo) {
        return new FsExternalFile(IFsFileType.Video, {
            ...opts,
            thumbnailUrl: addSuffixToUrl(zmlFile.url, '/frame0.png'),
            videoUrl: addSuffixToUrl(zmlFile.url, '/video.mp4'),
            // aspectRatio: (zmlFile as IFileStreamingVideo).output.aspectRatio || 1 # Don't use the output of the worker as it contains an invalid value
        })
    }
    if (zmlFile.type === IFileType.Video360) {
        return new FsExternalFile(IFsFileType.Video360, {
            ...opts,
            thumbnailUrl: addSuffixToUrl(zmlFile.url, '/frame0.png'),
            videoUrl: addSuffixToUrl(zmlFile.url, '/video.mp4'),
            // aspectRatio: (zmlFile as IFileStreamingVideo).output.aspectRatio || 1 # Don't use the output of the worker as it contains an invalid value
        })
    }
    if (zmlFile.type === IFileType.Audio) {
        return new FsExternalFile(IFsFileType.Audio, { ...opts, mp3Url: addSuffixToUrl(zmlFile.url, '/aud.mp3') })
    }
    throw new Error(`Unsupported file type: ${zmlFile.type}`)
}

/** Infers the filestore media type from a File's MIME type / extension when one isn't given. */
const inferFsFileType = (file: File): IFsFileType | undefined => {
    if (file.type.includes('image')) return IFsFileType.Image
    if (file.type.includes('video')) return IFsFileType.Video
    if (file.type.includes('audio')) return IFsFileType.Audio
    if (/\.(glb|gltf)$/i.test(file.name)) return IFsFileType.Model3d
    return undefined
}

/**
 * Wraps a locally downloaded File in an FsExternalFile with `blob:` URL previews, without touching
 * ZML. On import/toSpatialComponent the file is persisted into the project filestore. `type` should
 * be supplied by callers that already know it (media-library assets); it's otherwise inferred.
 */
export const fileToExternalFile = (file: File, type?: IFsFileType) => {
    const fsType = type ?? inferFsFileType(file)
    if (typeof fsType === 'undefined') throw new Error(`Unsupported file type: ${file.type}`)
    // The id has to be minted before the upload, because the `public-` prefix that makes video
    // readable without a JWT is part of the object path — see newFsFileId.
    const fileId = newFsFileId(fsType)
    const blobUrl = filestore.getBlobUrl(fileId, file)
    const opts = { source: FsExternalSource.Local, id: fileId, name: file.name, file }
    let fsFile: FsExternalFile | undefined;
    if (fsType === IFsFileType.Image) {
        fsFile = new FsExternalFile(IFsFileType.Image, { ...opts, thumbnailUrl: blobUrl })
    } else if (fsType === IFsFileType.Video) {
        fsFile = new FsExternalFile(IFsFileType.Video, { ...opts, thumbnailUrl: '', videoUrl: blobUrl })
    } else if (fsType === IFsFileType.Audio) {
        fsFile = new FsExternalFile(IFsFileType.Audio, { ...opts, thumbnailUrl: '', mp3Url: blobUrl })
    } else if (fsType === IFsFileType.Model3d) {
        fsFile = new FsExternalFile(IFsFileType.Model3d, { ...opts, thumbnailUrl: '' })
    }

    if (typeof fsFile === 'undefined') throw new Error(`Unsupported file type: ${file.type}`)
    filestore.registerExternalFile(fileId, fsFile)
    return fsFile
}
